/*
 * File: permitStatus.ts
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import moment from 'moment';

export type PermitStatus = 'valid' | 'expiring_soon' | 'expired';

export interface PermitStatusResult {
  status: PermitStatus;
  message: string;
}

/**
 * Determines the status of a student's permit based on expiration date.
 *
 * @param permitExpiration - Moment object representing permit expiration date
 * @returns Object with status ('valid' | 'expiring_soon' | 'expired') and message
 *
 * @example
 * const expiration = moment().add(15, 'days');
 * getPermitStatus(expiration)
 * // { status: 'expiring_soon', message: 'Permit expires in 15 days' }
 */
export function getPermitStatus(permitExpiration: moment.Moment | null): PermitStatusResult {
  if (!permitExpiration) {
    return { status: 'expired', message: 'No permit expiration date' };
  }

  const now = moment();
  const daysUntilExpiration = permitExpiration.diff(now, 'days');

  if (daysUntilExpiration < 0) {
    return { status: 'expired', message: 'Permit has expired' };
  }

  if (daysUntilExpiration <= 30) {
    return { status: 'expiring_soon', message: `Permit expires in ${daysUntilExpiration} days` };
  }

  return { status: 'valid', message: 'Permit is valid' };
}